import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSite } from '../data/SiteContext';
import { PERKS, pic } from '../data/site';

// Postcards section is off until the agency has real reviews to show.
const SHOW_TESTIMONIALS = false;

const scrollBy = (ref, dir) => {
  const el = ref.current;
  if (el) el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 300), behavior:'smooth' });
};

function CarouselControls({ carouselRef, allPath, allLabel }) {
  const navigate = useNavigate();
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
      <button className="arrow-btn" onClick={() => scrollBy(carouselRef, -1)}>‹</button>
      <button className="arrow-btn" onClick={() => scrollBy(carouselRef, 1)}>›</button>
      <span className="btn-outline" onClick={() => navigate(allPath)}>{allLabel}</span>
    </div>
  );
}

/**
 * A testimonial's trip label ("Punta Cana, 7 nights") starts with a destination name.
 * Match it against what we actually sell and link it; fall back to plain text otherwise.
 */
function TripLink({ trip }) {
  const { dests } = useSite();
  const match = trip && dests.find(d => trip.toLowerCase().includes(d.name.toLowerCase()));
  if (!match) return <div style={{ fontSize:13, color:'#5a726f' }}>{trip}</div>;
  return (
    <Link to={'/destinations/' + match.slug} style={{ display:'block', fontSize:13, color:'#0e8a9c', fontWeight:600 }}>{trip} →</Link>
  );
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const parseDay = (v) => {
  const [y, m, d] = (v || '').split('-').map(Number);
  return v && y && m && d ? { y, m, d } : null;
};
const dayLabel = (p) => `${p.d} ${MONTHS[p.m - 1]} ${p.y}`;

/** Human-readable travel window for the WhatsApp quote — "14–21 Nov 2026", not ISO. */
function dateRangeLabel(fromValue, toValue) {
  const f = parseDay(fromValue);
  const t = parseDay(toValue);
  if (!f && !t) return '';
  if (f && !t) return `from ${dayLabel(f)}`;
  if (!f && t) return `until ${dayLabel(t)}`;
  if (f.y === t.y && f.m === t.m) return `${f.d}–${t.d} ${MONTHS[f.m - 1]} ${f.y}`;
  if (f.y === t.y) return `${f.d} ${MONTHS[f.m - 1]} – ${dayLabel(t)}`;
  return `${dayLabel(f)} – ${dayLabel(t)}`;
}

const todayValue = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const REEL_SIZE = 5;
const REEL_MS = 5500;

/** Five destinations spread across the list, so the reel crosses regions instead of
 *  showing the first five. Destinations without a real cover are left out — their
 *  `img` is a generated placeholder. */
function reelSlides(dests) {
  const pool = dests.filter(d => d.coverImageFileId);
  if (pool.length <= REEL_SIZE) return pool;
  return Array.from({ length: REEL_SIZE }, (_, i) => pool[Math.floor((i * pool.length) / REEL_SIZE)]);
}

function DestinationReel({ slides }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced] = useState(() => typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const dotsRef = useRef(null);
  const running = !paused && !reduced && slides.length > 1;

  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => setIdx(i => (i + 1) % slides.length), REEL_MS);
    return () => clearTimeout(t);
  }, [idx, running, slides.length]);

  // A timer must not burn through slides in a tab nobody is looking at.
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  if (!slides.length) return null;
  const cur = slides[idx];

  const step = (dir) => {
    const next = (idx + dir + slides.length) % slides.length;
    setIdx(next);
    const dot = dotsRef.current && dotsRef.current.children[next];
    if (dot) dot.focus();
  };

  return (
    <div
      className="reel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Link to={'/destinations/' + cur.slug} className="reel__stage" aria-label={`${cur.name}, ${cur.country}`}>
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.img}
            alt={i === idx ? `${s.name}, ${s.country}` : ''}
            loading={i === 0 ? 'eager' : 'lazy'}
            className={'reel__img' + (i === idx ? ' reel__img--on' : '')}
          />
        ))}
        <span className="reel__veil" />
        <span className="reel__tag">{cur.tag}</span>
        <span className="reel__cap">
          <span className="reel__country">{cur.country}</span>
          <span className="reel__name serif">{cur.name}</span>
          <span className="reel__meta">{cur.nights ? `${cur.nights} nights · see the trip` : 'See the trip'} →</span>
        </span>
      </Link>
      <div
        className="reel__dots"
        ref={dotsRef}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          step(e.key === 'ArrowRight' ? 1 : -1);
        }}
      >
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`Show ${s.name}`}
            aria-current={i === idx}
            className={'reel__dot' + (i === idx ? ' reel__dot--on' : '')}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
      <div className="reel__bar">
        <i key={idx} className={running ? 'reel__bar-run' : ''} style={{ animationDuration: `${REEL_MS}ms` }} />
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { dests, tours, cruises, events, posts, testimonials, contact, banners } = useSite();
  // Real numbers, computed from live data — no invented marketing stats.
  const stats = [
    { value: String(dests.length || '—'), label: 'Destinations', path: '/destinations' },
    { value: String(new Set(dests.map(d => d.country)).size || '—'), label: 'Countries', path: '/regions' },
    { value: String((tours.length + cruises.length) || '—'), label: 'Tours & cruises', path: '/tours' },
    { value: String(events.filter(e => e.isActive).length || '—'), label: 'Events & festivals', path: '/experiences' },
  ];
  const reel = reelSlides(dests);
  const destNames = [...dests].sort((a, b) => a.name.localeCompare(b.name)).map(d => `${d.name}, ${d.country}`);
  const [findDest, setFindDest] = useState('Anywhere warm — surprise us');
  const [findWhen, setFindWhen] = useState('Flexible');
  const [findFrom, setFindFrom] = useState('');
  const [findTo, setFindTo] = useState('');
  const [findFlexDates, setFindFlexDates] = useState(true);
  const [findWho, setFindWho] = useState('2 adults');
  // "Specific dates" turns the preset into a real window; every other choice stays a preset.
  const exactDates = findWhen === 'dates';
  const datesInvalid = exactDates && !!findFrom && !!findTo && findTo < findFrom;
  const whenText = (() => {
    if (!exactDates) return findWhen;
    const range = dateRangeLabel(findFrom, findTo);
    if (!range) return 'Flexible';
    return findFlexDates ? `${range} (dates flexible)` : range;
  })();
  const quoteLink = `${contact.waLink}?text=${encodeURIComponent(`Hi! I'd like a quote — ${findDest}; travelling: ${whenText}; travellers: ${findWho}.`)}`;
  // The hero picture is admin-controlled: the lowest-positioned Live banner that actually
  // carries an image wins. mapBanner already substitutes a placeholder for imageless banners,
  // so require imageFileId rather than trusting img. No banner ready -> the placeholder stays.
  const heroBanner = banners
    .filter(b => (b.state === 'Live' || b.state === 2) && b.imageFileId)
    .sort((a, b) => a.position - b.position)[0];
  const heroImg = heroBanner ? heroBanner.img : pic('caribbean,beach,turquoise,palm', 1, 900, 900);
  const heroAlt = heroBanner ? (heroBanner.title || 'Featured destination') : 'Golden-hour beach escape';

  const destRef = useRef(null);
  const toursRef = useRef(null);
  const blogRef = useRef(null);

  return (
    <div>
      {/* Hero */}
      <section style={{ position:'relative', margin:'-88px 0 0', padding:'118px 24px 40px', overflow:'hidden', background:'linear-gradient(165deg,#eafaf3 0%,#c9ede6 46%,#a6e2d7 100%)' }}>
        <div style={{ position:'absolute', top:-120, right:-60, width:460, height:460, borderRadius:'50%', background:'radial-gradient(circle,#ffdd55,#f5b301)', opacity:0.5, filter:'blur(6px)' }}></div>
        <div style={{ position:'relative', maxWidth:1240, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:40, alignItems:'center' }}>
          <div className="float-up">
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'7px 16px', borderRadius:100, background:'rgba(255,255,255,0.7)', color:'#0e8a9c', border:'1px solid rgba(255,255,255,0.9)', fontSize:12.5, fontWeight:800, letterSpacing:'.06em', textTransform:'uppercase' }}>Beach resorts · Flights + hotels · Honeymoons</div>
            <h1 className="serif" style={{ fontSize:'clamp(38px,5vw,58px)', lineHeight:1.06, margin:'18px 0 16px', letterSpacing:'-0.5px', color:'#0c2b2f' }}>The Caribbean is our expertise. The world is our <em style={{ fontStyle:'italic', color:'#f5b301' }}>playground</em>.</h1>
            <p style={{ fontSize:'clamp(16px,2vw,19px)', lineHeight:1.55, color:'#5a726f', maxWidth:500, margin:'0 0 28px' }}>From tropical island escapes and luxury cruises to cultural adventures and unforgettable events around the world. Every journey is designed around you.</p>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              <a href={contact.waLink} target="_blank" rel="noreferrer" className="btn btn-teal">Get my quote →</a>
              <span className="btn btn-white" onClick={() => navigate('/destinations')}>Explore destinations</span>
            </div>
          </div>
          <div style={{ position:'relative', height:'clamp(320px,44vh,440px)', borderRadius:26, overflow:'hidden', boxShadow:'0 24px 54px rgba(16,90,90,0.4)', animation:'floatUp .9s ease both' }}>
            <img src={heroImg} alt={heroAlt} className="imgfill" />
            <div className="cover" style={{ background:'linear-gradient(180deg,rgba(255,210,63,0.14),rgba(6,40,45,0.30))' }}></div>
          </div>
        </div>
        {/* Inquiry bar */}
        <div className="find-bar" style={{ position:'relative', animation:'floatUp 1s ease both', maxWidth:1240, margin:'34px auto 0', padding:16, borderRadius:24, background:'#ffffff', border:'1px solid #f2e2d2', boxShadow:'0 20px 50px rgba(6,40,45,0.2)', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:10, alignItems:'end' }}>
          <label style={{ display:'block' }}>
            <span className="field-label">Destination</span>
            <select className="field-select" value={findDest} onChange={e => setFindDest(e.target.value)}>
              <option>Anywhere warm — surprise us</option>
              {destNames.map(n => <option key={n}>{n}</option>)}
            </select>
          </label>
          <label style={{ display:'block' }}>
            <span className="field-label">When</span>
            <select className="field-select" value={findWhen} onChange={e => setFindWhen(e.target.value)}>
              <option>Flexible</option><option>This month</option><option>Next 3 months</option><option>Later this year</option><option>Next year</option><option value="dates">Specific dates…</option>
            </select>
          </label>
          <label style={{ display:'block' }}>
            <span className="field-label">Travelers</span>
            <select className="field-select" value={findWho} onChange={e => setFindWho(e.target.value)}>
              <option>2 adults</option><option>2 adults, 2 kids</option><option>Solo</option><option>Group / 4+</option>
            </select>
          </label>
          <a href={quoteLink} target="_blank" rel="noreferrer" aria-disabled={datesInvalid} onClick={e => { if (datesInvalid) e.preventDefault(); }} className={'find-cta' + (datesInvalid ? '' : ' hover-bright')} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'14px 18px', borderRadius:14, fontWeight:800, fontSize:14.5, color:'#fff', background:'linear-gradient(135deg,#26c6a3,#0e8a9c)', boxShadow:'0 10px 22px rgba(14,138,122,0.4)', opacity: datesInvalid ? 0.5 : 1, pointerEvents: datesInvalid ? 'none' : 'auto' }}>Get my quote →</a>

          {/* "Specific dates" flows two date fields into the same grid, so they line up
              with the selects above instead of stretching across the whole card. */}
          {exactDates && (
            <>
              <div className="find-divider" />
              <label style={{ display:'block' }}>
                <span className="field-label">From</span>
                <input type="date" className="field-date" min={todayValue()} value={findFrom} onChange={e => setFindFrom(e.target.value)} />
              </label>
              <label style={{ display:'block' }}>
                <span className="field-label">To</span>
                <input type="date" className="field-date" min={findFrom || todayValue()} value={findTo} onChange={e => setFindTo(e.target.value)} />
              </label>
              <div className="find-flex">
                <input type="checkbox" id="find-flex-dates" checked={findFlexDates} onChange={e => setFindFlexDates(e.target.checked)} />
                <label htmlFor="find-flex-dates">Flexible by a few days</label>
              </div>
              {datesInvalid && (
                <p className="find-err">The return date can’t be before the departure date.</p>
              )}
            </>
          )}
        </div>
      </section>

      {/* Trust strip */}
      <section className="wrap">
        <div style={{ marginTop:-34, position:'relative', zIndex:5, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:2, borderRadius:22, overflow:'hidden', background:'#ffffff', border:'1px solid #f2e2d2', boxShadow:'0 14px 34px rgba(16,90,90,0.12)' }}>
          {stats.map(s => (
            <div key={s.label} onClick={() => navigate(s.path)} className="hover-bright"
              style={{ padding:'22px 20px', textAlign:'center', cursor:'pointer' }}>
              <div className="serif" style={{ fontSize:30, color:'#0e8a9c' }}>{s.value}</div>
              <div style={{ fontSize:12.5, fontWeight:600, color:'#5a726f', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured destinations */}
      <section className="wrap reveal" style={{ paddingTop:72, paddingBottom:20 }}>
        <div className="section-head">
          <div>
            <div className="kicker">Where to next</div>
            <h2 className="h2">Featured destinations</h2>
          </div>
          <CarouselControls carouselRef={destRef} allPath="/destinations" allLabel="View all →" />
        </div>
        <div ref={destRef} className="hscroll" style={{ gap:22 }}>
          {dests.map(d => (
            <div key={d.id} className="lift" onClick={() => navigate('/destinations/' + d.slug)} style={{ cursor:'pointer', position:'relative', borderRadius:24, overflow:'hidden', minHeight:340, flex:'0 0 320px', scrollSnapAlign:'start', display:'flex', flexDirection:'column', justifyContent:'flex-end', boxShadow:'0 14px 34px rgba(16,90,90,0.14)' }}>
              <div className="cover"><div className="bgimg" style={{ backgroundImage:`url(${d.img})` }}></div></div>
              <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0) 30%,rgba(6,40,45,0.78) 100%)' }}></div>
              <div style={{ position:'absolute', top:14, left:14, padding:'6px 12px', borderRadius:100, background:'rgba(255,255,255,0.25)', border:'1px solid rgba(255,255,255,0.4)', color:'#fff', fontSize:11.5, fontWeight:700, letterSpacing:'.04em' }}>{d.tag}</div>
              <div style={{ position:'relative', padding:18, color:'#fff' }}>
                <div style={{ fontSize:12.5, fontWeight:600, opacity:.9 }}>{d.country}</div>
                <div className="serif" style={{ fontSize:27, lineHeight:1.05, margin:'2px 0 8px' }}>{d.name}</div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, opacity:.92 }}>{d.nights} nights</span>
                  <span style={{ fontWeight:800, fontSize:15 }}>{d.from ? `from $${d.from}` : 'price on request'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tours preview */}
      <section className="wrap reveal" style={{ paddingTop:56, paddingBottom:20 }}>
        <div className="section-head">
          <div>
            <div className="kicker">Day by day</div>
            <h2 className="h2">Tours & excursions</h2>
          </div>
          <CarouselControls carouselRef={toursRef} allPath="/tours" allLabel="All →" />
        </div>
        <div ref={toursRef} className="hscroll" style={{ gap:20 }}>
          {tours.map(t => (
            <div key={t.name} className="card" style={{ borderRadius:22, overflow:'hidden', flex:'0 0 300px', scrollSnapAlign:'start', boxShadow:'0 12px 28px rgba(16,90,90,0.1)' }}>
              <div style={{ height:180 }}><div className="bgimg" style={{ backgroundImage:`url(${t.img})` }}></div></div>
              <div style={{ padding:18 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:700, color:'#0e9c86', textTransform:'uppercase', letterSpacing:'.05em' }}>{t.place} · {t.dur}</div>
                <div className="serif" style={{ fontSize:21, margin:'6px 0 12px', lineHeight:1.15 }}>{t.name}</div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:800, color:'#0c2b2f' }}>{t.price ? <>{`$${t.price}`} <span style={{ fontWeight:600, color:'#5a726f', fontSize:13 }}>/ person</span></> : <span style={{ fontSize:13.5 }}>price on request</span>}</span>
                  <a href={contact.waLink} target="_blank" rel="noreferrer" style={{ fontSize:13, fontWeight:700, color:'#0e8a9c', padding:'8px 12px', borderRadius:10, background:'rgba(14,138,122,0.1)' }}>Book →</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="wrap reveal" style={{ paddingTop:64, paddingBottom:64 }}>
        <div style={{ borderRadius:28, padding:44, background:'linear-gradient(135deg,rgba(31,181,106,0.14),rgba(14,138,156,0.12))', border:'1px solid #f2e2d2' }}>
          <h2 className="serif" style={{ fontSize:'clamp(28px,3.6vw,40px)', margin:'0 0 8px', textAlign:'center', letterSpacing:'-0.5px' }}>Why travelers choose {contact.brand}</h2>
          <p style={{ textAlign:'center', color:'#5a726f', maxWidth:560, margin:'0 auto 34px', fontSize:16 }}>Real people, real destinations, zero booking-site guesswork.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(230px,1fr))', gap:18 }}>
            {PERKS.map(p => (
              <div key={p.n} className="card" style={{ padding:'26px 22px', borderRadius:20, boxShadow:'0 8px 22px rgba(16,90,90,0.08)' }}>
                <div className="serif" style={{ width:46, height:46, borderRadius:14, background:'linear-gradient(140deg,#26c6a3,#0e8a9c)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:20, marginBottom:14 }}>{p.n}</div>
                <div style={{ fontWeight:800, fontSize:17, marginBottom:6 }}>{p.title}</div>
                <div style={{ color:'#5a726f', fontSize:14, lineHeight:1.55 }}>{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Horizontal media feature */}
      <section className="wrap reveal" style={{ paddingTop:20, paddingBottom:44 }}>
        <div className="card-cool" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:0, borderRadius:26, overflow:'hidden', boxShadow:'0 18px 44px rgba(16,90,90,0.14)' }}>
          <DestinationReel slides={reel} />
          <div style={{ padding:'clamp(28px,4vw,48px)', display:'flex', flexDirection:'column', justifyContent:'center' }}>
            <div className="kicker">Behind every trip</div>
            <h2 className="serif" style={{ fontSize:'clamp(28px,3.4vw,40px)', margin:'10px 0 12px', letterSpacing:'-0.5px', lineHeight:1.08 }}>See the places before you ever pack a bag.</h2>
            <p style={{ fontSize:16, lineHeight:1.6, color:'#5a726f', margin:'0 0 22px', maxWidth:'44ch' }}>A rolling look at the destinations we book most — every photo is somewhere we have actually sent travelers. Tap one to see the trip behind it.</p>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              <a href={contact.waLink} target="_blank" rel="noreferrer" className="btn btn-teal" style={{ padding:'14px 24px', boxShadow:'0 10px 22px rgba(16,90,90,0.28)' }}>Plan a trip like this →</a>
              <span className="btn btn-soft" onClick={() => navigate('/destinations')}>Browse destinations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials — hidden until real, verifiable reviews are collected (operator, 2026-08-16).
          The data, admin screen and this markup stay; flip SHOW_TESTIMONIALS to bring it back. */}
      {SHOW_TESTIMONIALS && <section className="wrap reveal" style={{ paddingTop:20, paddingBottom:64 }}>
        <h2 className="serif" style={{ fontSize:'clamp(28px,3.6vw,40px)', margin:'0 0 26px', letterSpacing:'-0.5px' }}>Postcards from happy travelers</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20 }}>
          {testimonials.map(r => (
            <div key={r.name} className="card" style={{ padding:26, borderRadius:22, boxShadow:'0 10px 26px rgba(16,90,90,0.1)' }}>
              <div style={{ color:'#e0a13a', fontSize:16, letterSpacing:2 }}>★★★★★</div>
              <p style={{ fontSize:15.5, lineHeight:1.6, color:'#0c2b2f', margin:'12px 0 18px' }}>"{r.text}"</p>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:44, height:44, borderRadius:'50%', background:'linear-gradient(140deg,#26c6a3,#0e8a9c)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800 }}>{r.initial}</div>
                <div>
                  <div style={{ fontWeight:700 }}>{r.name}</div>
                  {/* The trip label names a destination we sell — let the reader go and look at it. */}
                  <TripLink trip={r.trip} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>}

      {/* Blog preview */}
      <section className="wrap reveal" style={{ paddingBottom:64 }}>
        <div className="section-head">
          <h2 className="serif" style={{ fontSize:'clamp(28px,3.6vw,40px)', margin:0, letterSpacing:'-0.5px' }}>Travel guides & tips</h2>
          <CarouselControls carouselRef={blogRef} allPath="/blog" allLabel="All →" />
        </div>
        <div ref={blogRef} className="hscroll" style={{ gap:22 }}>
          {posts.map(b => (
            <div key={b.title} className="card lift3" onClick={() => navigate('/blog')} style={{ cursor:'pointer', borderRadius:22, overflow:'hidden', flex:'0 0 320px', scrollSnapAlign:'start', boxShadow:'0 10px 26px rgba(16,90,90,0.08)' }}>
              <div style={{ height:180 }}><div className="bgimg" style={{ backgroundImage:`url(${b.img})` }}></div></div>
              <div style={{ padding:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#0e9c86', textTransform:'uppercase', letterSpacing:'.06em' }}>{b.cat} · {b.mins} min read</div>
                <div className="serif" style={{ fontSize:21, margin:'8px 0 6px', lineHeight:1.2 }}>{b.title}</div>
                <div style={{ fontSize:13, color:'#5a726f' }}>{b.date}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="wrap reveal" style={{ paddingBottom:72 }}>
        <div style={{ position:'relative', borderRadius:30, overflow:'hidden', padding:'60px 40px', textAlign:'center' }}>
          <div className="cover"><img src={pic('sunset,ocean,hammock', 2, 1600, 900)} alt="Sunset over the ocean" className="imgfill" /></div>
          <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0.55),rgba(6,40,45,0.7))' }}></div>
          <div style={{ position:'relative', maxWidth:620, margin:'0 auto', color:'#fff' }}>
            <h2 className="serif" style={{ fontSize:'clamp(30px,4.4vw,48px)', margin:'0 0 12px', letterSpacing:'-0.5px' }}>Ready when you are.</h2>
            <p style={{ fontSize:17, lineHeight:1.6, color:'rgba(255,255,255,0.92)', margin:'0 0 26px' }}>Message us on WhatsApp and get a personalized itinerary within 24 hours — no obligation, no fees.</p>
            <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
              <a href={contact.waLink} target="_blank" rel="noreferrer" className="btn btn-green">Chat on WhatsApp</a>
              <a href={contact.telLink} className="btn" style={{ padding:'15px 26px', color:'#0e8a9c', background:'rgba(255,255,255,0.9)' }}>Call {contact.phone}</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
